import {CONFIGURA_EXPORT_OUTPUT_TYPE, describeMaterialOutputType, publishedMaterialOutputTypesFor} from "#material-outputs-publishing/targets/configura/payload"
import {z} from "zod"

type PublishableMaterialOutput = {
    type: string
    materialRevisionId: string | null
    resultDataObjectId: string | null
}

type MaterialOutputsIncoherenceReason =
    | {kind: "missing"; types: string[]}
    | {kind: "incomplete"; types: string[]}
    // Split from `incomplete`: an output with a file but no revision is not half-generated, it is of
    // unknown provenance. Two causes, indistinguishable — never stamped (outputs predating the
    // revision model), or the revision was deleted afterwards under `onDelete: SetNull`. Neither
    // justifies telling someone their exports are out of date, which is what the old wording did.
    | {kind: "unknownRevision"; types: string[]}
    | {kind: "duplicated"; types: string[]}
    | {kind: "revisionMismatch"; revisionByType: Record<string, string>}

type MaterialOutputsCoherenceResult =
    | {coherent: true; materialRevisionId: string; outputByType: Record<string, PublishableMaterialOutput>}
    | {coherent: false; reason: MaterialOutputsIncoherenceReason}

/**
 * `publishedTypes` is per-organization — it names the thumbnail size that organization publishes —
 * so this function reads only the types it is given. Anything else in `outputs` is inert, which is
 * what lets a caller fetch every publishable type in one query and narrow afterwards.
 */
function checkMaterialOutputsCoherence(
    outputs: readonly PublishableMaterialOutput[],
    publishedTypes: readonly string[],
): MaterialOutputsCoherenceResult {
    const byType = new Map<string, PublishableMaterialOutput[]>()
    for (const output of outputs) {
        byType.set(output.type, [...(byType.get(output.type) ?? []), output])
    }

    const missing = publishedTypes.filter((type) => (byType.get(type)?.length ?? 0) === 0)
    if (missing.length > 0) {
        return {coherent: false, reason: {kind: "missing", types: [...missing]}}
    }

    const duplicated = publishedTypes.filter((type) => (byType.get(type)?.length ?? 0) > 1)
    if (duplicated.length > 0) {
        return {coherent: false, reason: {kind: "duplicated", types: [...duplicated]}}
    }

    const outputByType: Record<string, PublishableMaterialOutput> = {}
    for (const type of publishedTypes) {
        const output = byType.get(type)?.[0]
        if (output) {
            outputByType[type] = output
        }
    }

    const incomplete = publishedTypes.filter((type) => !outputByType[type]?.resultDataObjectId)
    if (incomplete.length > 0) {
        return {coherent: false, reason: {kind: "incomplete", types: [...incomplete]}}
    }

    const unknownRevision = publishedTypes.filter((type) => !outputByType[type]?.materialRevisionId)
    if (unknownRevision.length > 0) {
        return {coherent: false, reason: {kind: "unknownRevision", types: [...unknownRevision]}}
    }

    const revisionByType: Record<string, string> = {}
    for (const type of publishedTypes) {
        const revisionId = outputByType[type]?.materialRevisionId
        if (revisionId) {
            revisionByType[type] = revisionId
        }
    }

    const revisions = new Set(Object.values(revisionByType))
    if (revisions.size !== 1) {
        return {coherent: false, reason: {kind: "revisionMismatch", revisionByType}}
    }

    const [materialRevisionId] = [...revisions]
    if (!materialRevisionId) {
        return {coherent: false, reason: {kind: "unknownRevision", types: [...publishedTypes]}}
    }
    return {coherent: true, materialRevisionId, outputByType}
}

/**
 * Why a publish cannot run, in the detail the classifier actually has. It used to be three strings,
 * with every coherence failure and "the exports are behind the material" collapsed into one of them —
 * so the single message asserted the outputs existed and were old, which is false when they are
 * missing and unknowable when their revision is null.
 */
export type NotPublishableReason =
    | {kind: "missingMaterialDatabaseProfile"}
    | {kind: "missingMaterialRevision"; publishedBefore: boolean}
    | {kind: "missingOutputs"; types: string[]}
    | {kind: "incompleteOutputs"; types: string[]}
    | {kind: "unknownOutputRevision"; types: string[]}
    | {kind: "duplicatedOutputs"; types: string[]}
    | {kind: "outputRevisionsDisagree"; revisionByType: Record<string, string>}
    | {kind: "outputsBehindMaterial"; outputRevisionId: string; latestCyclesRevisionId: string}

const describeTypes = (types: readonly string[]) => types.map(describeMaterialOutputType).join(", ")

/**
 * Rendered here rather than by each consumer: the publish task writes this straight onto the job row
 * and has no formatter, and the trigger mutation returns it as the error someone sees at the click.
 */
export function describeNotPublishableReason(reason: NotPublishableReason): string {
    switch (reason.kind) {
        case "missingMaterialDatabaseProfile":
            return "The organization has no Configura Material Database profile, so nothing can be published for it."
        case "missingMaterialRevision":
            return reason.publishedBefore
                ? "The material has no revision, but it has been published before."
                : "The material has no revision, so there is nothing to publish."
        case "missingOutputs":
            return `The material has not been exported yet: ${describeTypes(reason.types)} ${reason.types.length === 1 ? "is" : "are"} missing.`
        case "incompleteOutputs":
            return `Still being generated, or generated without a file: ${describeTypes(reason.types)}.`
        // Deliberately does not say "out of date" — nothing here knows that. See the note on the
        // `unknownRevision` coherence failure.
        case "unknownOutputRevision":
            return `Generated before we recorded which revision they came from: ${describeTypes(reason.types)}. Regenerate to publish.`
        case "duplicatedOutputs":
            return `More than one output exists for ${describeTypes(reason.types)}, so which to publish is ambiguous.`
        case "outputRevisionsDisagree":
            return `The outputs come from different revisions of the material: ${describeTypes(Object.keys(reason.revisionByType))}. Regenerate them together.`
        case "outputsBehindMaterial":
            return "The material has changed since its outputs were generated. Regenerate them, then publish."
        default:
            throw new Error(`Unknown NotPublishableReason kind: ${(reason as {kind: string}).kind}`)
    }
}

/** The coherence failure, restated as the reason publishing is blocked. */
function reasonForIncoherence(reason: MaterialOutputsIncoherenceReason): NotPublishableReason {
    switch (reason.kind) {
        case "missing":
            return {kind: "missingOutputs", types: reason.types}
        case "incomplete":
            return {kind: "incompleteOutputs", types: reason.types}
        case "unknownRevision":
            return {kind: "unknownOutputRevision", types: reason.types}
        case "duplicated":
            return {kind: "duplicatedOutputs", types: reason.types}
        case "revisionMismatch":
            return {kind: "outputRevisionsDisagree", revisionByType: reason.revisionByType}
    }
}

type EvaluatePublishStateInput = {
    outputs: readonly PublishableMaterialOutput[]
    latestCyclesRevisionId: string | null
    /**
     * Which thumbnail this organization publishes, from its Material Database profile. `null` means there is no
     * profile — the organization cannot publish at all, which is a state rather than a default to
     * substitute for: picking one here would compute a publishable verdict for an organization that
     * has no credential to publish with.
     */
    thumbnailMaterialOutputType: string | null
    binding?: {
        lastPublishedRevisionId?: string | null
        latestJobTaskRunning?: boolean
        latestJobTaskScheduled?: boolean
        latestJobTaskFailed?: boolean
    } | null
}

type PublishableState = {
    isPublishable: true
    materialRevisionId: string
    outputByType: Record<string, PublishableMaterialOutput>
}

type NonPublishableState = {
    isPublishable: false
    message: string
    reason: NotPublishableReason
    /**
     * The Configura export is the only thing in the way: everything else this organization publishes
     * exists, is complete, and came from the material's latest cycles revision. Publishing owns the
     * export's lifecycle, so the trigger answers this by generating one and waiting for it rather than
     * refusing — every other blocked reason needs a person to regenerate something first.
     */
    resolvableByGeneratingExport: boolean
}

type MaybePublishableState = PublishableState | NonPublishableState

export type PublishState =
    | {
          type: "notPublished"
          publishable: MaybePublishableState
      }
    | {
          type: "publishedButOutdated"
          publishable: MaybePublishableState
      }
    | {
          type: "publishedAndUpToDate"
      }
    // Every state but `publishedAndUpToDate` carries `publishable`, including the in-flight two.
    // That is not symmetry for its own sake: the publish task re-evaluates this at run time and
    // necessarily sees *itself* in flight, so an in-flight state that carried nothing would leave it
    // with no outputs to upload. It is also what a consumer asks after a failure — whether retrying
    // could work at all, which the failure itself does not answer.
    | {
          type: "publishingInProgress"
          publishable: MaybePublishableState
      }
    | {
          type: "publishingScheduled"
          publishable: MaybePublishableState
      }
    | {
          type: "publishingFailed"
          publishable: MaybePublishableState
      }

const publishableMaterialOutputSchema = z.object({
    type: z.string(),
    materialRevisionId: z.string().nullable(),
    resultDataObjectId: z.string().nullable(),
})

const notPublishableReasonSchema: z.ZodType<NotPublishableReason> = z.discriminatedUnion("kind", [
    z.object({kind: z.literal("missingMaterialDatabaseProfile")}),
    z.object({kind: z.literal("missingMaterialRevision"), publishedBefore: z.boolean()}),
    z.object({kind: z.literal("missingOutputs"), types: z.array(z.string())}),
    z.object({kind: z.literal("incompleteOutputs"), types: z.array(z.string())}),
    z.object({kind: z.literal("unknownOutputRevision"), types: z.array(z.string())}),
    z.object({kind: z.literal("duplicatedOutputs"), types: z.array(z.string())}),
    z.object({kind: z.literal("outputRevisionsDisagree"), revisionByType: z.record(z.string())}),
    z.object({kind: z.literal("outputsBehindMaterial"), outputRevisionId: z.string(), latestCyclesRevisionId: z.string()}),
])

const maybePublishableStateSchema: z.ZodType<MaybePublishableState> = z.discriminatedUnion("isPublishable", [
    z.object({
        isPublishable: z.literal(true),
        materialRevisionId: z.string(),
        outputByType: z.record(publishableMaterialOutputSchema),
    }),
    z.object({
        isPublishable: z.literal(false),
        message: z.string(),
        reason: notPublishableReasonSchema,
        resolvableByGeneratingExport: z.boolean(),
    }),
])

const publishStateSchema: z.ZodType<PublishState> = z.discriminatedUnion("type", [
    z.object({type: z.literal("notPublished"), publishable: maybePublishableStateSchema}),
    z.object({type: z.literal("publishedButOutdated"), publishable: maybePublishableStateSchema}),
    z.object({type: z.literal("publishedAndUpToDate")}),
    z.object({type: z.literal("publishingInProgress"), publishable: maybePublishableStateSchema}),
    z.object({type: z.literal("publishingScheduled"), publishable: maybePublishableStateSchema}),
    z.object({type: z.literal("publishingFailed"), publishable: maybePublishableStateSchema}),
])

export function parsePublishState(json: unknown): PublishState {
    return publishStateSchema.parse(json)
}

export function evaluatePublishState({outputs, latestCyclesRevisionId, thumbnailMaterialOutputType, binding}: EvaluatePublishStateInput): PublishState {
    // No binding row means nothing was ever bound, which is a different fact from a binding whose
    // last publish never succeeded. Both answer "the Material Database holds nothing" — but they must be
    // distinguished here rather than through an optional chain, because `binding?.x` collapses
    // "no binding" and "no value" into one `undefined` that no later comparison can tell apart.
    const lastPublishedRevisionId = binding ? (binding.lastPublishedRevisionId ?? null) : null

    const blocked = (reason: NotPublishableReason, resolvableByGeneratingExport = false): NonPublishableState => ({
        isPublishable: false,
        reason,
        message: describeNotPublishableReason(reason),
        resolvableByGeneratingExport,
    })

    const maybePublishable: MaybePublishableState = (() => {
        // Checked before anything about the material, because it is not a fact about the material.
        // Telling someone to regenerate outputs is the wrong advice when their organization has no
        // Material Database credential — and without a chosen thumbnail there is no set to check coherence against.
        if (thumbnailMaterialOutputType === null) {
            return blocked({kind: "missingMaterialDatabaseProfile"})
        }
        if (latestCyclesRevisionId === null) {
            return blocked({kind: "missingMaterialRevision", publishedBefore: binding !== null && binding !== undefined})
        }

        const publishedTypes = publishedMaterialOutputTypesFor(thumbnailMaterialOutputType)
        const outputsCoherence = checkMaterialOutputsCoherence(outputs, publishedTypes)
        if (!outputsCoherence.coherent) {
            // The same check over everything but the export. Coherence reports its *first* failure across
            // the whole set, so a missing export masks anything wrong with the other two — and the export
            // is the one deficiency publishing clears by itself, which makes it the least useful thing to
            // report. Whatever the reduced set says is what actually needs a person, so it wins.
            const withoutExport = checkMaterialOutputsCoherence(
                outputs,
                publishedTypes.filter((type) => type !== CONFIGURA_EXPORT_OUTPUT_TYPE),
            )
            if (!withoutExport.coherent) {
                return blocked(reasonForIncoherence(withoutExport.reason))
            }
            if (withoutExport.materialRevisionId !== latestCyclesRevisionId) {
                return blocked({
                    kind: "outputsBehindMaterial",
                    outputRevisionId: withoutExport.materialRevisionId,
                    latestCyclesRevisionId,
                })
            }
            // Everything else is current, so the failure is the export's alone. Duplicates are the one
            // kind generating another export cannot clear.
            return blocked(reasonForIncoherence(outputsCoherence.reason), outputsCoherence.reason.kind !== "duplicated")
        }
        // Coherent but behind the material — a different fact from the outputs disagreeing with each
        // other, and the common one. Kept apart because the advice differs: regenerate, rather than
        // work out which export is out of step.
        if (outputsCoherence.materialRevisionId !== latestCyclesRevisionId) {
            return blocked({
                kind: "outputsBehindMaterial",
                outputRevisionId: outputsCoherence.materialRevisionId,
                latestCyclesRevisionId,
            })
        }
        return {
            isPublishable: true,
            materialRevisionId: outputsCoherence.materialRevisionId,
            outputByType: outputsCoherence.outputByType,
        }
    })()

    if (binding?.latestJobTaskRunning) return {type: "publishingInProgress", publishable: maybePublishable}
    if (binding?.latestJobTaskScheduled) return {type: "publishingScheduled", publishable: maybePublishable}
    // A failed run outranks an up-to-date Material Database: the two only coincide when a redundant re-publish
    // failed after an earlier success, and since the Material Database has no DELETE, a half-completed write
    // is exactly the case where reporting "current" would be the more expensive lie.
    if (binding?.latestJobTaskFailed) return {type: "publishingFailed", publishable: maybePublishable}
    // `lastPublishedRevisionId !== null` guards the case where neither side has a revision: two
    // nulls are not agreement, they are two absences.
    if (lastPublishedRevisionId !== null && lastPublishedRevisionId === latestCyclesRevisionId) return {type: "publishedAndUpToDate"}

    return {
        type: lastPublishedRevisionId === null ? "notPublished" : "publishedButOutdated",
        publishable: maybePublishable,
    }
}
